// Markdown instructions handed to the remote AI agent builder. Base URL is filled from the running CRM.
export function buildAgentGuide(base, agentName = "Jasmine Ai") {
  return `# ${agentName} — Leprecon+ CRM API connection guide

## 1. What the agent can do
- Look up a client's order by **order number, email, phone or name**.
- Read **everything on the order** (status, vehicle, colour, tracking, dates, notes, history).
- **Add a customer-contact note** to the order (what the client asked / what was answered). Staff see it in the order's Notes and history.
- **Send the CRM's Status email** (only if the key was granted the \`orders:status_email\` permission): the standard Leprecon+ delivery-estimate email for the order's current stage, sent to the verified customer. One send per request, idempotent.
- Nothing else: no edits, no cancellations, no refunds, no customer data changes, no free-text emails.

## 2. Credentials
- A Super Admin creates the key in CRM → **Settings → External API — AI agents** and sends it to you once (format \`lp_live_…\`). Permissions (read / note / status email) are granted there too — never ask for credentials or permissions in chat.
- Store it as a secret (environment variable), never in prompts, logs or client messages.
- If the key leaks or is lost: tell the Super Admin, who revokes it and issues a new one. Revocation is immediate.
- Limit: **120 requests per minute** per key. Every call is logged (time, endpoint, order, IP).

## 3. Connection
Base URL: \`${base}\`

Send the key on **every** request, either header works:
\`\`\`
Authorization: Bearer lp_live_XXXXXXXX
X-API-Key: lp_live_XXXXXXXX
\`\`\`
All responses are JSON. Connection test:
\`\`\`bash
curl -H "Authorization: Bearer $LP_API_KEY" ${base}/me
# → {"ok":true,"key_name":"${agentName}","scopes":["orders:read","orders:note","orders:status_email"],"rate_limit_per_min":120,"server_time":"…"}
\`\`\`
If \`scopes\` lacks \`orders:status_email\`, the status-email endpoint answers 403 — ask the Super Admin to grant it in Settings.

## 4. Endpoints

### GET /me — key check
Returns key name, scopes and server time. Use at start-up and when debugging 401 errors.

### GET /statuses — status codes → human labels
\`\`\`json
{"statuses":[{"code":"lead","label":"Lead"},{"code":"pre_order","label":"Pre Order"},{"code":"pending","label":"Pending"},
{"code":"advance","label":"Advance"},{"code":"production","label":"Production"},{"code":"product_transfer","label":"Product Transfer"},
{"code":"packing","label":"Packing"},{"code":"inventory_packing","label":"Inv Packing"},{"code":"shipping","label":"Shipping"},
{"code":"completed","label":"Completed"},{"code":"fulfilled","label":"Order Fulfilled"},{"code":"cancelled","label":"Cancelled"},{"code":"na","label":"N/A"}]}
\`\`\`
Plain-language meaning for clients:
- lead / pre_order / pending / advance → order received, not in production yet
- production → covers are being made
- product_transfer → finished, moving to the packing warehouse
- packing / inventory_packing → being packed / checked
- shipping → shipped (tracking number is in \`summary.tracking\`)
- completed / fulfilled → delivered / closed
- cancelled → cancelled

### GET /orders/search?q=<text>&limit=20 — find orders
\`q\` is one value: order number (\`PO-5049\`, \`5049\`, Shopify #), email, phone (digits only is fine) or name. Minimum 3 characters. Max limit 50.
\`\`\`bash
curl -H "Authorization: Bearer $LP_API_KEY" "${base}/orders/search?q=PO-5049"
curl -H "Authorization: Bearer $LP_API_KEY" "${base}/orders/search?q=mark@example.com"
curl -H "Authorization: Bearer $LP_API_KEY" "${base}/orders/search?q=4165551234"
curl -H "Authorization: Bearer $LP_API_KEY" "${base}/orders/search?q=Mark%20Smith"
\`\`\`
Response (summaries, newest first):
\`\`\`json
{"query":"PO-5049","count":1,"orders":[{
  "id":"…","po_number":"PO-5049","shopify_order_number":"5049","customer_order_number":null,
  "status":"shipping","status_label":"Shipping","on_hold":false,"cancelled":false,
  "is_warranty":false,"is_reorder":false,"replacement_of_po":null,
  "customer":{"name":"Mark Smith","email":"mark@example.com","phone":"+1 416 555 1234"},
  "vehicle":{"brand":"Tesla","model":"Model Y","year":"2023","trim":null,"color":"Black","rim_size":"19"},
  "catalog_number":"CAT-1021",
  "tracking":{"number":"1Z…","carrier":"UPS","eta":"2026-06-20","url":"https://…"},
  "dates":{"created_at":"…","updated_at":"…","production_started_at":"…","packed_at":"…","shipped_at":"…","completed_at":null,"fulfilled_at":null,"cancelled_at":null},
  "ships_inside":"7-10 days","waiting_reply":false}]}
\`\`\`

### GET /orders/<ref> — full order
\`<ref>\` = PO number (\`PO-5049\`), bare number (\`5049\`), Shopify / customer order number or order id. Returns the whole order record plus \`summary\` (same shape as search) and \`status_label\`. Extra fields of interest: \`notes_log[]\`, \`status_history[]\`, \`agent_contacts[]\`, \`last_customer_contact_at\`.

\`discount\` (also inside \`summary\`): \`null\` when no discount was applied, otherwise \`{ code, codes[], total, currency, side_only, note }\`. \`side_only\` = \`"rear"\` / \`"front"\` when the customer used a *Rear pair only* / *Front pair only* discount — they bought ONE pair; the other side is not part of the order (no covers, no photos needed). Use \`note\` verbatim when a client asks why only one pair is listed.
\`\`\`bash
curl -H "Authorization: Bearer $LP_API_KEY" ${base}/orders/PO-5049
\`\`\`

### POST /orders/<ref>/contact-note — log the contact
\`\`\`bash
curl -X POST -H "Authorization: Bearer $LP_API_KEY" -H "Content-Type: application/json" \\
  -d '{"note":"Client asked for ETA. Told: shipped on UPS 1Z…, ETA Jun 20.","channel":"chat","customer_name":"Mark Smith","agent":"${agentName}"}' \\
  ${base}/orders/PO-5049/contact-note
# → {"ok":true,"po_number":"PO-5049","note_id":"…","at":"…"}
\`\`\`
Fields: \`note\` (required), \`channel\` (chat | phone | email | sms), \`customer_name\`, \`agent\` (defaults to the key name).
Log **one note per conversation** at the end, with the question and the answer given. Mention anything staff must act on (e.g. "client wants to change colour" or "client says package damaged").

### POST /orders/<ref>/status-email — send the Status email (permission required)
Sends Leprecon+'s **existing** status email: the saved delivery estimate for the order's current CRM stage (e.g. Production → "delivered within approximately 14–18 days"; Fulfilled → the final carrier tracking number). The text is fixed by the CRM — the agent cannot change it.
\`\`\`bash
curl -X POST -H "Authorization: Bearer $LP_API_KEY" -H "Content-Type: application/json" \\
  -H "Idempotency-Key: sdc-ticket-18422" \\
  -d '{"customer_email":"mark@example.com","request_ref":"sdc-ticket-18422","agent":"${agentName}"}' \\
  ${base}/orders/PO-5049/status-email
# → {"ok":true,"send_id":"8f1c…","duplicate":false,"po_number":"PO-5049","order_status":"production",
#    "order_status_label":"Production","option":"Production","estimate":"Your package is expected to be delivered within approximately 14–18 days.",
#    "to":"mark@example.com","subject":"Your Leprecon+ order #5049 — delivery update","sent_at":"…","idempotency_key":"sdc-ticket-18422"}
\`\`\`
Rules:
- \`customer_email\` (required) must be the e-mail **on the matched order** — this is the exact-match guard. A different address → **409 customer_mismatch**, nothing is sent.
- **Idempotency**: send \`Idempotency-Key\` (header) or \`idempotency_key\` (body) — use your ticket / conversation id. A retry with the same key returns the **same \`send_id\`** with \`"duplicate": true\` and does **not** resend. Without a key, a repeat for the same order inside 15 minutes is also treated as a retry.
- \`request_ref\` (optional) is written on the order so staff can trace the send back to your ticket.
- Use it only for **status-only** requests ("where is my order / when will it arrive"). Anything mixed (status + a change, a complaint, a refund…) → do **not** send; draft a reply for staff and log a contact-note.
- If the send takes long or times out, \`GET /sends/<send_id>\` (or simply retry with the same Idempotency-Key).

Errors specific to this endpoint:
| Code | \`code\` | Meaning |
|---|---|---|
| 403 | — | key has no \`orders:status_email\` permission |
| 409 | customer_mismatch | e-mail does not match the order's customer |
| 409 | no_customer_email | order has no customer e-mail on file — hand to staff |
| 409 | not_applicable | cancelled / N/A / no saved estimate for this stage — hand to staff |
| 409 | idempotency_conflict | the same Idempotency-Key was used for another order |
| 409 | send_in_progress | the same send is still running — retry in a few seconds |
| 502 | send_failed | mail server error — retry with the same Idempotency-Key |

### GET /sends/<send_id> — check a previous send
Returns the same shape as the POST (\`status\`: sent | failed).

## 5. Recommended conversation flow
1. Ask the client for an order number, or the email/phone used on the order.
2. \`GET /orders/search?q=…\`. If \`count\` is 0, ask for another identifier (email ↔ phone ↔ name). Do not guess.
3. If several orders match, ask the client which one (vehicle + date), or confirm a second identifier before reading details.
4. **Identity check before sharing details**: the client must give at least two matching facts (e.g. order number + email, or name + phone). Never read back an email/phone/address the client did not already provide.
5. \`GET /orders/<ref>\` and answer from \`summary\` (status_label, tracking, ETA, ships_inside, vehicle/colour).
6. Flags: \`on_hold\` → "order is paused, team will reach out"; \`waiting_reply\` → the team is waiting for the client's answer, ask them to check email; \`cancelled\` → cancelled; \`is_warranty\` / \`is_reorder\` → warranty or replacement order (\`replacement_of_po\`).
7. Finish with \`POST /orders/<ref>/contact-note\`.
8. **Status-only request** ("where is my order?", "when will it arrive?") from a verified client who wants it by e-mail → \`POST /orders/<ref>/status-email\` with the client's e-mail and your ticket id as Idempotency-Key, then tell the client the update was e-mailed. Anything mixed stays a draft for staff.
9. Anything you cannot do (change address/colour, refund, cancel, complaint): say the team will handle it and log it clearly in the note.

## 6. Errors
| Code | Meaning | What to do |
|---|---|---|
| 400 | bad query (q < 3 chars, empty note, missing customer_email) | fix the request |
| 401 | missing / invalid / revoked key | check header; ask Super Admin for a new key |
| 403 | key lacks the needed scope | ask Super Admin to grant \`orders:read\` / \`orders:note\` / \`orders:status_email\` in Settings |
| 404 | order not found | try another identifier via search |
| 409 | status-email guard (see table above) | do not retry blindly — read \`detail.code\` |
| 429 | over 120 req/min | wait and retry |

## 7. Privacy rules
- Only discuss the order of the person you have verified.
- Do not disclose internal notes, staff names, costs, supplier or factory information even though they are present in the full record; share order status, vehicle, tracking and dates only.
- Do not store client data outside the conversation.
`;
}
